/**
 * Partner promotions copy in the three operator languages.
 *
 * Like I7-B, the catalog lives in its own bounded vue-i18n instance and only
 * follows the application locale, resolved to `ro | ru | en` (anything else →
 * `ro`). The application catalog is not extended; the menu reads its label
 * through `promotionsMenuLabel()`.
 */
import {computed, unref, type ComputedRef} from "vue";
import {createI18n} from "vue-i18n";
import {elementLocaleFor} from "@/i18n/i7a";

export const PROMOTION_LOCALES = ["ro", "ru", "en"] as const;
export type PromotionLocale = typeof PROMOTION_LOCALES[number];
export const PROMOTION_DEFAULT_LOCALE: PromotionLocale = "ro";

type Catalog = {[key: string]: string | Catalog};

const ro: Catalog = {
  menu: "Promoții",
  common: {
    cancel: "Renunță",
    close: "Închide",
    retry: "Reîncearcă",
    optional: "opțional",
    none: "—",
    yes: "Da",
    no: "Nu",
    loading: "Se încarcă…",
  },
  status: {draft: "Ciornă", active: "Activă", paused: "Întreruptă", archived: "Arhivată"},
  kind: {automatic: "Automată", code: "Cu cod"},
  discountType: {percent: "Procent din preț", amount: "Sumă scăzută", fixed_price: "Preț fix"},
  discountSummary: {
    percent: "−{value}%",
    amount: "−{value} {currency}",
    fixed_price: "Preț fix {value} {currency}",
  },
  policy: {
    exclusive: "Nu se cumulează cu reducerile existente ale rutei",
    apply_after_legacy_sale: "Se aplică după reducerea existentă a rutei",
  },
  list: {
    title: "Promoții",
    subtitle: "Reduceri pe rutele companiei. Pregătiți-le aici, apoi activați-le când sunteți gata.",
    create: "Promoție nouă",
    searchLabel: "Caută după titlu sau cod",
    searchPlaceholder: "Titlu sau cod",
    search: "Caută",
    statusFilter: "Stare",
    kindFilter: "Tip",
    busFilter: "Rută",
    anyStatus: "Toate stările",
    anyKind: "Toate tipurile",
    anyBus: "Toate rutele",
    resetFilters: "Resetează filtrele",
    filtersLabel: "Filtre",
    colTitle: "Promoție",
    colStatus: "Stare",
    colDiscount: "Reducere",
    colPeriod: "Plecări",
    colScope: "Se aplică la",
    colUpdated: "Modificată",
    open: "Deschide {title}",
    emptyTitle: "Nu există încă promoții",
    emptyText: "Creați prima promoție. Rămâne ciornă până o activați.",
    emptyReadOnly: "Nu există promoții pentru această companie.",
    filteredEmptyTitle: "Nicio promoție nu corespunde",
    filteredEmptyText: "Schimbați sau resetați filtrele.",
    error: "Lista nu a putut fi încărcată.",
    staleWarning: "Lista afișată poate fi veche: actualizarea a eșuat.",
    pageOf: "Pagina {page} din {pages}",
    prev: "Pagina anterioară",
    next: "Pagina următoare",
    total: "Promoții găsite: {count}",
    anyDate: "Orice dată",
    dateRange: "{from} – {to}",
    allRoutes: "Toate rutele",
    scopeCount: "Rute: {buses} · Segmente: {segments}",
    code: "Cod: {code}",
    pagination: "Paginare",
  },
  company: {
    label: "Compania",
    choose: "Alegeți compania",
    searchLabel: "Caută compania",
    loadMore: "Mai multe companii",
    required: "Alegeți compania pentru care lucrați.",
    loadError: "Companiile nu au putut fi încărcate.",
    noResults: "Nicio companie găsită.",
    current: "Compania selectată: {title}",
    fallback: "Compania #{id}",
  },
  steps: {
    where: "Unde",
    when: "Când",
    discount: "Reducere",
    limits: "Limite",
    review: "Verificare",
    progress: "Pasul {current} din {total}: {name}",
    nav: "Pașii promoției",
    hasErrors: "are erori",
    accessible: "Pasul {number}: {name}",
    accessibleError: "Pasul {number}: {name}, are erori",
  },
  editor: {
    createTitle: "Promoție nouă",
    editTitle: "Promoție",
    back: "Înapoi",
    next: "Continuă",
    saveCreate: "Salvează ciorna",
    saveUpdate: "Salvează modificările",
    saving: "Se salvează…",
    created: "Promoția a fost salvată ca ciornă.",
    saved: "Modificările au fost salvate.",
    unsaved: "Modificări nesalvate",
    loadError: "Promoția nu a putut fi încărcată.",
    notFound: "Promoția nu există sau nu mai este disponibilă.",
    backToList: "Înapoi la listă",
    readOnly: "Puteți vedea această promoție, dar nu o puteți modifica.",
    archived: "Promoția este arhivată. Nu mai poate fi modificată.",
    statusLabel: "Stare: {status}",
    actions: "Acțiuni",
    statusTo: {active: "Activează", paused: "Întrerupe", archived: "Arhivează"},
    resume: "Reia",
    saveFirst: "Salvați modificările înainte de a schimba starea.",
    delete: "Șterge",
    statusChanged: "Starea a fost schimbată: {status}.",
    deleted: "Promoția a fost ștearsă.",
    fixErrors: "Verificați câmpurile marcate: {count}.",
    hydrationError: "Alegerile salvate nu au putut fi verificate. Reîncercați, sau eliminați-le și alegeți din nou.",
    hydrationInvalid: "Unele alegeri salvate nu mai sunt disponibile. Eliminați-le sau alegeți altele.",
    savedAt: "Ultima modificare: {date}",
  },
  fields: {
    title: "Titlul promoției",
    titleHint: "Vizibil doar pentru echipa dvs.",
    kind: "Cum se aplică",
    kindHint: "Automată: pentru toți clienții eligibili. Cu cod: doar cu codul introdus.",
    code: "Codul promoției",
    codeHint: "Maximum 64 de caractere.",
    scopeHeading: "Rute",
    allBuses: "Toate rutele companiei",
    allBusesHint: "Include și rutele adăugate ulterior.",
    buses: "Rute alese",
    busesHint: "Promoția se aplică pe toată ruta.",
    segments: "Segmente alese",
    segmentsHint: "Doar între două opriri ale unei rute.",
    segmentBus: "Ruta segmentului",
    segmentPair: "Segment",
    addSegment: "Adaugă segmentul",
    noTargets: "Nu ați ales încă nicio rută sau segment.",
    categoriesHeading: "Categorii de preț",
    fareCategories: "Categorii",
    fareCategoriesHint: "Promoția se aplică doar categoriilor alese.",
    platformsHeading: "Platforme de vânzare",
    allPlatforms: "Toate platformele eligibile",
    allPlatformsHint: "Include și platformele eligibile adăugate ulterior.",
    platforms: "Platforme alese",
    departureHeading: "Date de plecare",
    departureHint: "Ambele date sunt incluse. Lăsați gol pentru orice dată.",
    departureFrom: "De la",
    departureTo: "Până la",
    bookingHeading: "Perioada de rezervare",
    bookingToggle: "Limitează perioada în care se poate rezerva",
    bookingHint: "Ora locală în fusul orar ales. Sfârșitul nu este inclus.",
    bookingStart: "Începe la",
    bookingEnd: "Se termină la",
    timezone: "Fus orar",
    discountType: "Tip de reducere",
    discountValue: "Valoare",
    discountValuePercent: "Procent",
    discountValueHint: "Folosiți punctul pentru zecimale, de ex. 10.5",
    currency: "Valuta",
    floorHeading: "Preț minim (opțional)",
    floorHint: "Prețul nu scade sub această sumă. Completați suma și valuta, sau lăsați ambele goale.",
    floorAmount: "Suma minimă",
    floorCurrency: "Valuta prețului minim",
    priority: "Prioritate",
    priorityHint: "Când se potrivesc mai multe promoții, câștigă prioritatea mai mare.",
    policy: "Reduceri existente ale rutei",
    maxUses: "Utilizări în total",
    maxUsesPerCustomer: "Utilizări per client",
    limitHint: "Număr întreg între 1 și 2 000 000 000. Lăsați gol pentru fără limită.",
    limitsNote: "Limitele sunt salvate cu promoția. Numărarea utilizărilor nu este afișată aici.",
    noLimit: "Fără limită",
    releaseOnCancel: "Eliberează utilizarea când rezervarea este anulată",
  },
  schedule: {
    resolving: "Se verifică ora…",
    valid: "Corespunde cu {utc} UTC ({offset})",
    nonexistent: "Această oră nu există în fusul orar ales (ceasul se dă înainte). Alegeți altă oră.",
    ambiguous: "Această oră apare de două ori (ceasul se dă înapoi). Alegeți varianta corectă:",
    option: "Ora {offset} — {utc} UTC",
    stale: "Ora salvată nu mai corespunde. Alegeți din nou varianta corectă:",
    staleNone: "Ora salvată nu mai corespunde. Introduceți din nou ora.",
    error: "Ora nu a putut fi verificată.",
    departureDefault: "Fus orar implicit: {timezone}",
  },
  catalog: {
    choose: "Alege",
    change: "Schimbă",
    hide: "Ascunde lista",
    searchLabel: "Caută în {name}",
    loadMore: "Afișează mai multe",
    empty: "Niciun rezultat.",
    error: "Opțiunile nu au putut fi încărcate.",
    remove: "Elimină {label}",
    unavailable: "indisponibil",
    pending: "se încarcă denumirea",
    selectedCount: "Alese: {count}",
    platform: "Platformă #{id}",
    bus: "Ruta #{id}",
    currency: "Valuta #{id}",
    segment: "{from} → {to}",
    segmentOnBus: "{bus}: {from} → {to}",
    stop: "Oprirea #{id}",
    placeholder: "Nimic ales",
  },
  review: {
    heading: "Verificați promoția",
    intro: "Salvarea nu activează promoția. O puteți activa separat după salvare.",
    where: "Unde se aplică",
    when: "Când",
    discount: "Reducere",
    limits: "Limite",
    edit: "Modifică",
    allBuses: "Toate rutele companiei",
    buses: "Rute: {count}",
    segments: "Segmente: {count}",
    categories: "Categorii: {count}",
    allPlatforms: "Toate platformele eligibile",
    platforms: "Platforme: {count}",
    noneChosen: "Nimic ales",
    anyDeparture: "Orice dată de plecare",
    anyBooking: "Oricând",
    bookingRange: "{from} – {to} ({timezone})",
    floor: "Preț minim: {value} {currency}",
    noFloor: "Fără preț minim",
    priority: "Prioritate: {value}",
    unlimited: "Fără limită",
    releaseYes: "Utilizarea se eliberează la anulare",
    releaseNo: "Utilizarea nu se eliberează la anulare",
  },
  preview: {
    title: "Previzualizare ipotetică",
    badge: "Ipotetic",
    note: "Calcul de probă pentru o călătorie aleasă. Nu este un preț oferit clienților și nu salvează nimic.",
    context: "Călătoria de probă",
    bus: "Ruta",
    segment: "Segment",
    category: "Categorie de preț",
    platform: "Platformă",
    date: "Data plecării",
    quoteCurrency: "Valuta rezultatului",
    seatToggle: "Adaugă supliment de loc",
    seatAmount: "Supliment de loc",
    seatCurrency: "Valuta suplimentului",
    recalculate: "Recalculează previzualizarea",
    recalculating: "Se recalculează…",
    missing: "Pentru calcul mai lipsesc:",
    missingBus: "ruta",
    missingSegment: "segmentul",
    missingCategory: "categoria de preț",
    missingPlatform: "platforma",
    missingDate: "data plecării",
    missingSeat: "suma și valuta suplimentului",
    missingDraft: "câmpuri completate corect în promoție",
    idle: "Alegeți călătoria și apăsați „Recalculează previzualizarea”.",
    stale: "Promoția sau călătoria s-a schimbat. Rezultatul de mai jos este vechi.",
    refreshing: "Se recalculează. Rezultatul de mai jos este cel anterior.",
    notUpdated: "Recalcularea nu a reușit. Rezultatul de mai jos este cel anterior și nu a fost actualizat.",
    noBase: "Nu există un preț de bază pentru această călătorie.",
    error: "Previzualizarea nu a putut fi calculată.",
    base: "Preț de bază",
    sale: "Reducere existentă a rutei",
    discount: "Reducere promoție",
    supplement: "Supliment de loc",
    original: "Preț inițial",
    result: "Preț rezultat",
    pricedAt: "Calculat la {date}",
    winnerCandidate: "Această promoție ar câștiga.",
    winnerPersisted: "Ar câștiga altă promoție existentă (#{id}), nu aceasta.",
    winnerNone: "Nicio promoție nu s-ar aplica acestei călătorii.",
    diagnostics: "Observații",
    diag: {
      price_sort_unconverted: "Prețul nu a fost convertit pentru sortare.",
      no_applicable_bus_price: "Pentru această rută, segment, categorie și dată nu este stabilit un preț de bază.",
      fallback: "Observație de calcul: {code}",
    },
    toggle: "Arată sau ascunde previzualizarea",
  },
  dialog: {
    dirtyTitle: "Aveți modificări nesalvate",
    dirtyText: "Dacă plecați, modificările se pierd.",
    stay: "Rămân",
    leave: "Plec fără salvare",
    companyTitle: "Schimbați compania?",
    companyText: "Modificările nesalvate se pierd, iar alegerile legate de compania curentă se golesc.",
    companyConfirm: "Schimbă compania",
    parentTitle: "Goliți alegerile dependente?",
    parentAllBuses: "Alegerea „toate rutele” elimină rutele și segmentele alese ({count}).",
    parentAllPlatforms: "Alegerea „toate platformele” elimină platformele alese ({count}).",
    parentBookingOff: "Oprirea perioadei de rezervare golește orele alese.",
    parentConfirm: "Golește",
    statusTitle: "Confirmați schimbarea stării",
    statusText: "„{title}” va trece din {from} în {to}.",
    activateNote: "Promoția activă se aplică doar când prețurile promoționale sunt pornite pentru companie.",
    archiveNote: "Arhivarea este definitivă: promoția nu mai poate fi modificată sau reactivată.",
    deleteTitle: "Ștergeți promoția?",
    deleteText: "„{title}” va fi ștearsă din listă. Istoricul modificărilor se păstrează.",
    deleteConfirm: "Șterge promoția",
    conflictTitle: "Promoția a fost modificată între timp",
    conflictText: "Nu am salvat nimic. Comparați versiunea salvată cu a dvs., apoi decideți.",
    conflictServer: "Versiunea salvată",
    conflictMine: "Versiunea dvs. (nesalvată)",
    conflictKeep: "Păstrez versiunea mea",
    conflictLoad: "Încarc versiunea salvată",
    conflictRetryExhausted: "Serverul a fost ocupat și nu a salvat. Încercați din nou peste puțin timp.",
    notFoundTitle: "Promoția nu mai este disponibilă",
    notFoundText: "Aveți modificări nesalvate. Le puteți vedea încă, dar nu mai pot fi salvate aici.",
    discard: "Renunț la modificări",
  },
  errors: {
    required: "Câmp obligatoriu.",
    too_long: "Textul este prea lung.",
    decimal: "Introduceți un număr cu cel mult 4 zecimale, de ex. 10.5",
    pair: "Completați și suma, și valuta, sau lăsați ambele goale.",
    integer: "Introduceți un număr întreg.",
    range: "Valoarea nu este în intervalul permis.",
    date_order: "Data de sfârșit nu poate fi înaintea celei de început.",
    time_order: "Sfârșitul trebuie să fie după început.",
    unresolved: "Confirmați ora aleasă.",
    too_many: "Ați ales prea multe (maximum 500).",
    limit: "Introduceți un număr întreg între 1 și 2 000 000 000, sau lăsați gol.",
    format: "Valoarea nu are formatul așteptat.",
    unavailable: "Una sau mai multe alegeri nu mai sunt disponibile.",
    unexpected: "Câmpul nu este acceptat.",
    server: "Serverul a refuzat această valoare.",
    network: "Serverul nu a putut fi contactat. Verificați conexiunea și reîncercați.",
    forbidden: "Nu aveți permisiunea pentru această acțiune.",
    unauthenticated: "Sesiunea a expirat. Autentificați-vă din nou.",
    invalid_response: "Serverul a trimis un răspuns neașteptat. Reîncercați.",
    server_error: "A apărut o eroare pe server. Reîncercați.",
    generic: "A apărut o eroare. Reîncercați.",
    conflict: "Starea promoției nu permite această acțiune.",
    not_found: "Promoția nu există sau nu mai este disponibilă.",
    validation: "Unele câmpuri trebuie corectate.",
  },
};

const ru: Catalog = {
  menu: "Акции",
  common: {
    cancel: "Отмена",
    close: "Закрыть",
    retry: "Повторить",
    optional: "необязательно",
    none: "—",
    yes: "Да",
    no: "Нет",
    loading: "Загрузка…",
  },
  status: {draft: "Черновик", active: "Активна", paused: "Приостановлена", archived: "В архиве"},
  kind: {automatic: "Автоматическая", code: "По коду"},
  discountType: {percent: "Процент от цены", amount: "Сумма скидки", fixed_price: "Фиксированная цена"},
  discountSummary: {
    percent: "−{value}%",
    amount: "−{value} {currency}",
    fixed_price: "Фикс. цена {value} {currency}",
  },
  policy: {
    exclusive: "Не суммируется с существующими скидками маршрута",
    apply_after_legacy_sale: "Применяется после существующей скидки маршрута",
  },
  list: {
    title: "Акции",
    subtitle: "Скидки на маршрутах компании. Подготовьте их здесь и активируйте, когда будете готовы.",
    create: "Новая акция",
    searchLabel: "Поиск по названию или коду",
    searchPlaceholder: "Название или код",
    search: "Найти",
    statusFilter: "Статус",
    kindFilter: "Тип",
    busFilter: "Маршрут",
    anyStatus: "Все статусы",
    anyKind: "Все типы",
    anyBus: "Все маршруты",
    resetFilters: "Сбросить фильтры",
    filtersLabel: "Фильтры",
    colTitle: "Акция",
    colStatus: "Статус",
    colDiscount: "Скидка",
    colPeriod: "Отправления",
    colScope: "Действует на",
    colUpdated: "Изменена",
    open: "Открыть {title}",
    emptyTitle: "Акций пока нет",
    emptyText: "Создайте первую акцию. Она останется черновиком, пока вы её не активируете.",
    emptyReadOnly: "У этой компании нет акций.",
    filteredEmptyTitle: "Нет подходящих акций",
    filteredEmptyText: "Измените или сбросьте фильтры.",
    error: "Не удалось загрузить список.",
    staleWarning: "Показанный список может быть устаревшим: обновление не удалось.",
    pageOf: "Страница {page} из {pages}",
    prev: "Предыдущая страница",
    next: "Следующая страница",
    total: "Найдено акций: {count}",
    anyDate: "Любая дата",
    dateRange: "{from} – {to}",
    allRoutes: "Все маршруты",
    scopeCount: "Маршруты: {buses} · Сегменты: {segments}",
    code: "Код: {code}",
    pagination: "Постраничная навигация",
  },
  company: {
    label: "Компания",
    choose: "Выберите компанию",
    searchLabel: "Поиск компании",
    loadMore: "Ещё компании",
    required: "Выберите компанию, с которой работаете.",
    loadError: "Не удалось загрузить компании.",
    noResults: "Компании не найдены.",
    current: "Выбрана компания: {title}",
    fallback: "Компания #{id}",
  },
  steps: {
    where: "Где",
    when: "Когда",
    discount: "Скидка",
    limits: "Лимиты",
    review: "Проверка",
    progress: "Шаг {current} из {total}: {name}",
    nav: "Шаги акции",
    hasErrors: "есть ошибки",
    accessible: "Шаг {number}: {name}",
    accessibleError: "Шаг {number}: {name}, есть ошибки",
  },
  editor: {
    createTitle: "Новая акция",
    editTitle: "Акция",
    back: "Назад",
    next: "Далее",
    saveCreate: "Сохранить черновик",
    saveUpdate: "Сохранить изменения",
    saving: "Сохранение…",
    created: "Акция сохранена как черновик.",
    saved: "Изменения сохранены.",
    unsaved: "Есть несохранённые изменения",
    loadError: "Не удалось загрузить акцию.",
    notFound: "Акция не существует или больше недоступна.",
    backToList: "Назад к списку",
    readOnly: "Вы можете просматривать эту акцию, но не изменять её.",
    archived: "Акция в архиве. Её больше нельзя изменить.",
    statusLabel: "Статус: {status}",
    actions: "Действия",
    statusTo: {active: "Активировать", paused: "Приостановить", archived: "В архив"},
    resume: "Возобновить",
    saveFirst: "Сохраните изменения, прежде чем менять статус.",
    delete: "Удалить",
    statusChanged: "Статус изменён: {status}.",
    deleted: "Акция удалена.",
    fixErrors: "Проверьте отмеченные поля: {count}.",
    hydrationError: "Не удалось проверить сохранённые варианты. Повторите или удалите их и выберите заново.",
    hydrationInvalid: "Некоторые сохранённые варианты больше недоступны. Удалите их или выберите другие.",
    savedAt: "Последнее изменение: {date}",
  },
  fields: {
    title: "Название акции",
    titleHint: "Видно только вашей команде.",
    kind: "Как применяется",
    kindHint: "Автоматическая: для всех подходящих клиентов. По коду: только с введённым кодом.",
    code: "Код акции",
    codeHint: "Не более 64 символов.",
    scopeHeading: "Маршруты",
    allBuses: "Все маршруты компании",
    allBusesHint: "Включает и маршруты, добавленные позже.",
    buses: "Выбранные маршруты",
    busesHint: "Акция действует на весь маршрут.",
    segments: "Выбранные сегменты",
    segmentsHint: "Только между двумя остановками маршрута.",
    segmentBus: "Маршрут сегмента",
    segmentPair: "Сегмент",
    addSegment: "Добавить сегмент",
    noTargets: "Вы ещё не выбрали ни маршрута, ни сегмента.",
    categoriesHeading: "Ценовые категории",
    fareCategories: "Категории",
    fareCategoriesHint: "Акция действует только на выбранные категории.",
    platformsHeading: "Платформы продаж",
    allPlatforms: "Все подходящие платформы",
    allPlatformsHint: "Включает и подходящие платформы, добавленные позже.",
    platforms: "Выбранные платформы",
    departureHeading: "Даты отправления",
    departureHint: "Обе даты включительно. Оставьте пустым для любой даты.",
    departureFrom: "С",
    departureTo: "По",
    bookingHeading: "Период бронирования",
    bookingToggle: "Ограничить период, когда можно бронировать",
    bookingHint: "Местное время в выбранном часовом поясе. Конец не включается.",
    bookingStart: "Начало",
    bookingEnd: "Окончание",
    timezone: "Часовой пояс",
    discountType: "Тип скидки",
    discountValue: "Значение",
    discountValuePercent: "Процент",
    discountValueHint: "Для дробной части используйте точку, например 10.5",
    currency: "Валюта",
    floorHeading: "Минимальная цена (необязательно)",
    floorHint: "Цена не опустится ниже этой суммы. Укажите сумму и валюту или оставьте оба поля пустыми.",
    floorAmount: "Минимальная сумма",
    floorCurrency: "Валюта минимальной цены",
    priority: "Приоритет",
    priorityHint: "Если подходят несколько акций, побеждает более высокий приоритет.",
    policy: "Существующие скидки маршрута",
    maxUses: "Всего использований",
    maxUsesPerCustomer: "Использований на клиента",
    limitHint: "Целое число от 1 до 2 000 000 000. Оставьте пустым — без лимита.",
    limitsNote: "Лимиты сохраняются вместе с акцией. Подсчёт использований здесь не показывается.",
    noLimit: "Без лимита",
    releaseOnCancel: "Возвращать использование при отмене бронирования",
  },
  schedule: {
    resolving: "Проверяем время…",
    valid: "Соответствует {utc} UTC ({offset})",
    nonexistent: "Этого времени нет в выбранном часовом поясе (перевод часов вперёд). Выберите другое время.",
    ambiguous: "Это время встречается дважды (перевод часов назад). Выберите нужный вариант:",
    option: "Время {offset} — {utc} UTC",
    stale: "Сохранённое время больше не совпадает. Выберите нужный вариант заново:",
    staleNone: "Сохранённое время больше не совпадает. Введите время заново.",
    error: "Не удалось проверить время.",
    departureDefault: "Часовой пояс по умолчанию: {timezone}",
  },
  catalog: {
    choose: "Выбрать",
    change: "Изменить",
    hide: "Скрыть список",
    searchLabel: "Поиск: {name}",
    loadMore: "Показать ещё",
    empty: "Ничего не найдено.",
    error: "Не удалось загрузить варианты.",
    remove: "Удалить {label}",
    unavailable: "недоступно",
    pending: "загрузка названия",
    selectedCount: "Выбрано: {count}",
    platform: "Платформа #{id}",
    bus: "Маршрут #{id}",
    currency: "Валюта #{id}",
    segment: "{from} → {to}",
    segmentOnBus: "{bus}: {from} → {to}",
    stop: "Остановка #{id}",
    placeholder: "Ничего не выбрано",
  },
  review: {
    heading: "Проверьте акцию",
    intro: "Сохранение не активирует акцию. Её можно активировать отдельно после сохранения.",
    where: "Где действует",
    when: "Когда",
    discount: "Скидка",
    limits: "Лимиты",
    edit: "Изменить",
    allBuses: "Все маршруты компании",
    buses: "Маршруты: {count}",
    segments: "Сегменты: {count}",
    categories: "Категории: {count}",
    allPlatforms: "Все подходящие платформы",
    platforms: "Платформы: {count}",
    noneChosen: "Ничего не выбрано",
    anyDeparture: "Любая дата отправления",
    anyBooking: "В любое время",
    bookingRange: "{from} – {to} ({timezone})",
    floor: "Минимальная цена: {value} {currency}",
    noFloor: "Без минимальной цены",
    priority: "Приоритет: {value}",
    unlimited: "Без лимита",
    releaseYes: "Использование возвращается при отмене",
    releaseNo: "Использование не возвращается при отмене",
  },
  preview: {
    title: "Гипотетический предпросмотр",
    badge: "Гипотетически",
    note: "Пробный расчёт для выбранной поездки. Это не цена для клиентов, и ничего не сохраняется.",
    context: "Пробная поездка",
    bus: "Маршрут",
    segment: "Сегмент",
    category: "Ценовая категория",
    platform: "Платформа",
    date: "Дата отправления",
    quoteCurrency: "Валюта результата",
    seatToggle: "Добавить доплату за место",
    seatAmount: "Доплата за место",
    seatCurrency: "Валюта доплаты",
    recalculate: "Пересчитать предпросмотр",
    recalculating: "Пересчитываем…",
    missing: "Для расчёта не хватает:",
    missingBus: "маршрута",
    missingSegment: "сегмента",
    missingCategory: "ценовой категории",
    missingPlatform: "платформы",
    missingDate: "даты отправления",
    missingSeat: "суммы и валюты доплаты",
    missingDraft: "правильно заполненных полей акции",
    idle: "Выберите поездку и нажмите «Пересчитать предпросмотр».",
    stale: "Акция или поездка изменились. Результат ниже устарел.",
    refreshing: "Идёт пересчёт. Ниже показан предыдущий результат.",
    notUpdated: "Пересчитать не удалось. Ниже показан предыдущий результат, он не обновлён.",
    noBase: "Для этой поездки нет базовой цены.",
    error: "Не удалось рассчитать предпросмотр.",
    base: "Базовая цена",
    sale: "Существующая скидка маршрута",
    discount: "Скидка по акции",
    supplement: "Доплата за место",
    original: "Исходная цена",
    result: "Итоговая цена",
    pricedAt: "Рассчитано {date}",
    winnerCandidate: "Эта акция победила бы.",
    winnerPersisted: "Победила бы другая существующая акция (#{id}), а не эта.",
    winnerNone: "К этой поездке не применилась бы ни одна акция.",
    diagnostics: "Замечания",
    diag: {
      price_sort_unconverted: "Цена не была конвертирована для сортировки.",
      no_applicable_bus_price: "Для этого маршрута, сегмента, категории и даты не задана базовая цена.",
      fallback: "Замечание расчёта: {code}",
    },
    toggle: "Показать или скрыть предпросмотр",
  },
  dialog: {
    dirtyTitle: "Есть несохранённые изменения",
    dirtyText: "Если вы уйдёте, изменения будут потеряны.",
    stay: "Остаться",
    leave: "Уйти без сохранения",
    companyTitle: "Сменить компанию?",
    companyText: "Несохранённые изменения будут потеряны, а выбор, связанный с текущей компанией, очищен.",
    companyConfirm: "Сменить компанию",
    parentTitle: "Очистить зависимый выбор?",
    parentAllBuses: "Выбор «все маршруты» удалит выбранные маршруты и сегменты ({count}).",
    parentAllPlatforms: "Выбор «все платформы» удалит выбранные платформы ({count}).",
    parentBookingOff: "Отключение периода бронирования очистит выбранное время.",
    parentConfirm: "Очистить",
    statusTitle: "Подтвердите смену статуса",
    statusText: "«{title}» перейдёт из статуса {from} в {to}.",
    activateNote: "Активная акция применяется, только когда для компании включены акционные цены.",
    archiveNote: "Архивирование окончательно: акцию больше нельзя изменить или активировать.",
    deleteTitle: "Удалить акцию?",
    deleteText: "«{title}» будет удалена из списка. История изменений сохранится.",
    deleteConfirm: "Удалить акцию",
    conflictTitle: "Акция была изменена в это время",
    conflictText: "Ничего не сохранено. Сравните сохранённую версию со своей и решите.",
    conflictServer: "Сохранённая версия",
    conflictMine: "Ваша версия (не сохранена)",
    conflictKeep: "Оставить мою версию",
    conflictLoad: "Загрузить сохранённую версию",
    conflictRetryExhausted: "Сервер был занят и не сохранил изменения. Попробуйте ещё раз чуть позже.",
    notFoundTitle: "Акция больше недоступна",
    notFoundText: "У вас есть несохранённые изменения. Их можно посмотреть, но сохранить здесь уже нельзя.",
    discard: "Отказаться от изменений",
  },
  errors: {
    required: "Обязательное поле.",
    too_long: "Слишком длинный текст.",
    decimal: "Введите число не более чем с 4 знаками после точки, например 10.5",
    pair: "Укажите и сумму, и валюту или оставьте оба поля пустыми.",
    integer: "Введите целое число.",
    range: "Значение вне допустимого диапазона.",
    date_order: "Дата окончания не может быть раньше даты начала.",
    time_order: "Окончание должно быть позже начала.",
    unresolved: "Подтвердите выбранное время.",
    too_many: "Выбрано слишком много (не более 500).",
    limit: "Введите целое число от 1 до 2 000 000 000 или оставьте пустым.",
    format: "Значение имеет неверный формат.",
    unavailable: "Один или несколько вариантов больше недоступны.",
    unexpected: "Поле не поддерживается.",
    server: "Сервер отклонил это значение.",
    network: "Не удалось связаться с сервером. Проверьте подключение и повторите.",
    forbidden: "У вас нет прав на это действие.",
    unauthenticated: "Сессия истекла. Войдите заново.",
    invalid_response: "Сервер прислал неожиданный ответ. Повторите.",
    server_error: "Ошибка на сервере. Повторите.",
    generic: "Произошла ошибка. Повторите.",
    conflict: "Статус акции не позволяет это действие.",
    not_found: "Акция не существует или больше недоступна.",
    validation: "Некоторые поля нужно исправить.",
  },
};

const en: Catalog = {
  menu: "Promotions",
  common: {
    cancel: "Cancel",
    close: "Close",
    retry: "Retry",
    optional: "optional",
    none: "—",
    yes: "Yes",
    no: "No",
    loading: "Loading…",
  },
  status: {draft: "Draft", active: "Active", paused: "Paused", archived: "Archived"},
  kind: {automatic: "Automatic", code: "With code"},
  discountType: {percent: "Percent off the price", amount: "Amount off", fixed_price: "Fixed price"},
  discountSummary: {
    percent: "−{value}%",
    amount: "−{value} {currency}",
    fixed_price: "Fixed price {value} {currency}",
  },
  policy: {
    exclusive: "Does not combine with existing route discounts",
    apply_after_legacy_sale: "Applies after the existing route discount",
  },
  list: {
    title: "Promotions",
    subtitle: "Discounts on your company's routes. Prepare them here, then activate them when you are ready.",
    create: "New promotion",
    searchLabel: "Search by title or code",
    searchPlaceholder: "Title or code",
    search: "Search",
    statusFilter: "Status",
    kindFilter: "Type",
    busFilter: "Route",
    anyStatus: "All statuses",
    anyKind: "All types",
    anyBus: "All routes",
    resetFilters: "Reset filters",
    filtersLabel: "Filters",
    colTitle: "Promotion",
    colStatus: "Status",
    colDiscount: "Discount",
    colPeriod: "Departures",
    colScope: "Applies to",
    colUpdated: "Updated",
    open: "Open {title}",
    emptyTitle: "No promotions yet",
    emptyText: "Create your first promotion. It stays a draft until you activate it.",
    emptyReadOnly: "This company has no promotions.",
    filteredEmptyTitle: "No promotion matches",
    filteredEmptyText: "Change or reset the filters.",
    error: "The list could not be loaded.",
    staleWarning: "The list shown may be out of date: refreshing failed.",
    pageOf: "Page {page} of {pages}",
    prev: "Previous page",
    next: "Next page",
    total: "Promotions found: {count}",
    anyDate: "Any date",
    dateRange: "{from} – {to}",
    allRoutes: "All routes",
    scopeCount: "Routes: {buses} · Segments: {segments}",
    code: "Code: {code}",
    pagination: "Pagination",
  },
  company: {
    label: "Company",
    choose: "Choose the company",
    searchLabel: "Search company",
    loadMore: "More companies",
    required: "Choose the company you are working for.",
    loadError: "Companies could not be loaded.",
    noResults: "No company found.",
    current: "Selected company: {title}",
    fallback: "Company #{id}",
  },
  steps: {
    where: "Where",
    when: "When",
    discount: "Discount",
    limits: "Limits",
    review: "Review",
    progress: "Step {current} of {total}: {name}",
    nav: "Promotion steps",
    hasErrors: "has errors",
    accessible: "Step {number}: {name}",
    accessibleError: "Step {number}: {name}, has errors",
  },
  editor: {
    createTitle: "New promotion",
    editTitle: "Promotion",
    back: "Back",
    next: "Continue",
    saveCreate: "Save draft",
    saveUpdate: "Save changes",
    saving: "Saving…",
    created: "The promotion was saved as a draft.",
    saved: "Your changes were saved.",
    unsaved: "Unsaved changes",
    loadError: "The promotion could not be loaded.",
    notFound: "The promotion does not exist or is no longer available.",
    backToList: "Back to the list",
    readOnly: "You can view this promotion but you cannot change it.",
    archived: "This promotion is archived. It can no longer be changed.",
    statusLabel: "Status: {status}",
    actions: "Actions",
    statusTo: {active: "Activate", paused: "Pause", archived: "Archive"},
    resume: "Resume",
    saveFirst: "Save your changes before changing the status.",
    delete: "Delete",
    statusChanged: "Status changed: {status}.",
    deleted: "The promotion was deleted.",
    fixErrors: "Check the highlighted fields: {count}.",
    hydrationError: "The saved choices could not be checked. Retry, or remove them and choose again.",
    hydrationInvalid: "Some saved choices are no longer available. Remove them or choose others.",
    savedAt: "Last updated: {date}",
  },
  fields: {
    title: "Promotion title",
    titleHint: "Only visible to your team.",
    kind: "How it applies",
    kindHint: "Automatic: for every eligible customer. With code: only when the code is entered.",
    code: "Promotion code",
    codeHint: "Up to 64 characters.",
    scopeHeading: "Routes",
    allBuses: "All company routes",
    allBusesHint: "Also includes routes added later.",
    buses: "Chosen routes",
    busesHint: "The promotion applies to the whole route.",
    segments: "Chosen segments",
    segmentsHint: "Only between two stops of a route.",
    segmentBus: "Segment route",
    segmentPair: "Segment",
    addSegment: "Add segment",
    noTargets: "You have not chosen a route or segment yet.",
    categoriesHeading: "Fare categories",
    fareCategories: "Categories",
    fareCategoriesHint: "The promotion applies only to the chosen categories.",
    platformsHeading: "Sales platforms",
    allPlatforms: "All eligible platforms",
    allPlatformsHint: "Also includes eligible platforms added later.",
    platforms: "Chosen platforms",
    departureHeading: "Departure dates",
    departureHint: "Both dates are included. Leave empty for any date.",
    departureFrom: "From",
    departureTo: "To",
    bookingHeading: "Booking period",
    bookingToggle: "Limit when bookings can be made",
    bookingHint: "Local time in the chosen time zone. The end is not included.",
    bookingStart: "Starts at",
    bookingEnd: "Ends at",
    timezone: "Time zone",
    discountType: "Discount type",
    discountValue: "Value",
    discountValuePercent: "Percent",
    discountValueHint: "Use a dot for decimals, e.g. 10.5",
    currency: "Currency",
    floorHeading: "Minimum price (optional)",
    floorHint: "The price never goes below this amount. Fill in both amount and currency, or leave both empty.",
    floorAmount: "Minimum amount",
    floorCurrency: "Minimum price currency",
    priority: "Priority",
    priorityHint: "When several promotions match, the higher priority wins.",
    policy: "Existing route discounts",
    maxUses: "Total uses",
    maxUsesPerCustomer: "Uses per customer",
    limitHint: "Whole number from 1 to 2,000,000,000. Leave empty for no limit.",
    limitsNote: "Limits are saved with the promotion. Usage counts are not shown here.",
    noLimit: "No limit",
    releaseOnCancel: "Give the use back when the booking is cancelled",
  },
  schedule: {
    resolving: "Checking the time…",
    valid: "Matches {utc} UTC ({offset})",
    nonexistent: "This time does not exist in the chosen time zone (clocks move forward). Choose another time.",
    ambiguous: "This time happens twice (clocks move back). Choose the right one:",
    option: "Time {offset} — {utc} UTC",
    stale: "The saved time no longer matches. Choose the right one again:",
    staleNone: "The saved time no longer matches. Enter the time again.",
    error: "The time could not be checked.",
    departureDefault: "Default time zone: {timezone}",
  },
  catalog: {
    choose: "Choose",
    change: "Change",
    hide: "Hide list",
    searchLabel: "Search {name}",
    loadMore: "Show more",
    empty: "No results.",
    error: "The options could not be loaded.",
    remove: "Remove {label}",
    unavailable: "unavailable",
    pending: "loading name",
    selectedCount: "Chosen: {count}",
    platform: "Platform #{id}",
    bus: "Route #{id}",
    currency: "Currency #{id}",
    segment: "{from} → {to}",
    segmentOnBus: "{bus}: {from} → {to}",
    stop: "Stop #{id}",
    placeholder: "Nothing chosen",
  },
  review: {
    heading: "Review the promotion",
    intro: "Saving does not activate the promotion. You can activate it separately after saving.",
    where: "Where it applies",
    when: "When",
    discount: "Discount",
    limits: "Limits",
    edit: "Edit",
    allBuses: "All company routes",
    buses: "Routes: {count}",
    segments: "Segments: {count}",
    categories: "Categories: {count}",
    allPlatforms: "All eligible platforms",
    platforms: "Platforms: {count}",
    noneChosen: "Nothing chosen",
    anyDeparture: "Any departure date",
    anyBooking: "Any time",
    bookingRange: "{from} – {to} ({timezone})",
    floor: "Minimum price: {value} {currency}",
    noFloor: "No minimum price",
    priority: "Priority: {value}",
    unlimited: "No limit",
    releaseYes: "The use is given back on cancellation",
    releaseNo: "The use is not given back on cancellation",
  },
  preview: {
    title: "Hypothetical preview",
    badge: "Hypothetical",
    note: "A trial calculation for a chosen trip. It is not a price offered to customers and nothing is saved.",
    context: "Trial trip",
    bus: "Route",
    segment: "Segment",
    category: "Fare category",
    platform: "Platform",
    date: "Departure date",
    quoteCurrency: "Result currency",
    seatToggle: "Add a seat supplement",
    seatAmount: "Seat supplement",
    seatCurrency: "Supplement currency",
    recalculate: "Recalculate preview",
    recalculating: "Recalculating…",
    missing: "Still needed for the calculation:",
    missingBus: "the route",
    missingSegment: "the segment",
    missingCategory: "the fare category",
    missingPlatform: "the platform",
    missingDate: "the departure date",
    missingSeat: "the supplement amount and currency",
    missingDraft: "correctly filled promotion fields",
    idle: "Choose a trip and press “Recalculate preview”.",
    stale: "The promotion or the trip changed. The result below is out of date.",
    refreshing: "Recalculating. The result below is the previous one.",
    notUpdated: "The recalculation failed. The result below is the previous one and was not updated.",
    noBase: "There is no base price for this trip.",
    error: "The preview could not be calculated.",
    base: "Base price",
    sale: "Existing route discount",
    discount: "Promotion discount",
    supplement: "Seat supplement",
    original: "Original price",
    result: "Resulting price",
    pricedAt: "Calculated at {date}",
    winnerCandidate: "This promotion would win.",
    winnerPersisted: "Another existing promotion (#{id}) would win, not this one.",
    winnerNone: "No promotion would apply to this trip.",
    diagnostics: "Notes",
    diag: {
      price_sort_unconverted: "The price was not converted for sorting.",
      no_applicable_bus_price: "No base price is set for this route, segment, fare category and date.",
      fallback: "Calculation note: {code}",
    },
    toggle: "Show or hide the preview",
  },
  dialog: {
    dirtyTitle: "You have unsaved changes",
    dirtyText: "If you leave, your changes will be lost.",
    stay: "Stay",
    leave: "Leave without saving",
    companyTitle: "Switch company?",
    companyText: "Unsaved changes will be lost and choices tied to the current company will be cleared.",
    companyConfirm: "Switch company",
    parentTitle: "Clear dependent choices?",
    parentAllBuses: "Choosing “all routes” removes the chosen routes and segments ({count}).",
    parentAllPlatforms: "Choosing “all platforms” removes the chosen platforms ({count}).",
    parentBookingOff: "Turning off the booking period clears the chosen times.",
    parentConfirm: "Clear",
    statusTitle: "Confirm the status change",
    statusText: "“{title}” will move from {from} to {to}.",
    activateNote: "An active promotion only applies when promotional pricing is switched on for the company.",
    archiveNote: "Archiving is final: the promotion can no longer be changed or reactivated.",
    deleteTitle: "Delete the promotion?",
    deleteText: "“{title}” will be removed from the list. The change history is kept.",
    deleteConfirm: "Delete promotion",
    conflictTitle: "The promotion was changed in the meantime",
    conflictText: "Nothing was saved. Compare the saved version with yours, then decide.",
    conflictServer: "Saved version",
    conflictMine: "Your version (unsaved)",
    conflictKeep: "Keep my version",
    conflictLoad: "Load the saved version",
    conflictRetryExhausted: "The server was busy and did not save. Try again in a moment.",
    notFoundTitle: "The promotion is no longer available",
    notFoundText: "You have unsaved changes. You can still see them, but they can no longer be saved here.",
    discard: "Discard changes",
  },
  errors: {
    required: "Required field.",
    too_long: "The text is too long.",
    decimal: "Enter a number with up to 4 decimals, e.g. 10.5",
    pair: "Fill in both the amount and the currency, or leave both empty.",
    integer: "Enter a whole number.",
    range: "The value is outside the allowed range.",
    date_order: "The end date cannot be before the start date.",
    time_order: "The end must be after the start.",
    unresolved: "Confirm the chosen time.",
    too_many: "Too many chosen (maximum 500).",
    limit: "Enter a whole number from 1 to 2,000,000,000, or leave it empty.",
    format: "The value does not have the expected format.",
    unavailable: "One or more choices are no longer available.",
    unexpected: "This field is not accepted.",
    server: "The server refused this value.",
    network: "The server could not be reached. Check your connection and retry.",
    forbidden: "You do not have permission for this action.",
    unauthenticated: "Your session has expired. Please sign in again.",
    invalid_response: "The server sent an unexpected response. Please retry.",
    server_error: "Something went wrong on the server. Please retry.",
    generic: "Something went wrong. Please retry.",
    conflict: "The promotion's status does not allow this action.",
    not_found: "The promotion does not exist or is no longer available.",
    validation: "Some fields need to be corrected.",
  },
};

export const PROMOTION_MESSAGES: Readonly<Record<PromotionLocale, Catalog>> = {ro, ru, en};

/** Fixed server field messages → local error codes; anything else is `server`. */
const SERVER_MESSAGE_CODES: Readonly<Record<string, string>> = {
  "The field is required.": "required",
  "The field format is invalid.": "format",
  "The field is outside the allowed range.": "range",
  "Unexpected field.": "unexpected",
  "One or more selected values are not available.": "unavailable",
};

export const serverMessageCode = (message: string | undefined): string =>
  (message && SERVER_MESSAGE_CODES[message]) || "server";

export function resolvePromotionLocale(value: unknown): PromotionLocale {
  return (PROMOTION_LOCALES as readonly unknown[]).includes(value) ? (value as PromotionLocale) : PROMOTION_DEFAULT_LOCALE;
}

const catalog = createI18n({
  legacy: false,
  locale: PROMOTION_DEFAULT_LOCALE,
  fallbackLocale: PROMOTION_DEFAULT_LOCALE,
  missingWarn: false,
  fallbackWarn: false,
  messages: {
    ro: {promotions: PROMOTION_MESSAGES.ro},
    ru: {promotions: PROMOTION_MESSAGES.ru},
    en: {promotions: PROMOTION_MESSAGES.en},
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GlobalComposer = any;
let registered: {global: GlobalComposer} | null = null;

export function registerPromotionsI18n(instance: {global: GlobalComposer}): void {
  registered = instance;
}

const currentLocale = (): PromotionLocale =>
  resolvePromotionLocale(registered ? unref(registered.global.locale) : PROMOTION_DEFAULT_LOCALE);

/**
 * Menu label for the current application locale. Read at render time, so the
 * menu follows runtime locale switches while the application catalog stays
 * untouched.
 */
export const promotionsMenuLabel = (): string => PROMOTION_MESSAGES[currentLocale()].menu as string;

export interface PromotionsI18n {
  t: (key: string, named?: Record<string, unknown>) => string;
  has: (key: string) => boolean;
  tError: (code: string | null | undefined) => string;
  tDiagnostic: (code: string) => string;
  formatInstant: (value: string | null) => string;
  formatDate: (value: string | null) => string;
  formatUtc: (value: string) => string;
  pick: (label: {ro: string; ru: string; en: string}) => string;
  platformLabel: (platform: {id: number; name: string | null; short: string | null}) => string;
  segmentLabel: (segment: {from_stop_id: number; to_stop_id: number; from_label: LabelSet; to_label: LabelSet}) => string;
  categoryLabel: (category: {id: number; label: LabelSet}) => string;
  locale: ComputedRef<PromotionLocale>;
  elementLocale: ComputedRef<ReturnType<typeof elementLocaleFor>>;
}

type LabelSet = {ro: string; ru: string; en: string};

const nonBlank = (value: string | null | undefined): string => (value && value.trim() ? value : "");

const UTC_PARTS =/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})\.(\d{3})\d{3}Z$/;

const epochOf = (value: string): number | null => {
  const m = UTC_PARTS.exec(value);
  if (!m) return null;
  return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6], +m[7]);
};

export function usePromotionsI18n(): PromotionsI18n {
  const global = catalog.global;
  const locale = computed(currentLocale);
  const elementLocale = computed(() => elementLocaleFor(locale.value));
  const has = (key: string) => global.te(`promotions.${key}`, locale.value);
  const t = (key: string, named: Record<string, unknown> = {}): string =>
    global.t(`promotions.${key}`, named, {locale: locale.value});
  const tError = (code: string | null | undefined): string =>
    code && has(`errors.${code}`) ? t(`errors.${code}`) : t("errors.generic");
  const tDiagnostic = (code: string): string =>
    code !== "fallback" && has(`preview.diag.${code}`) ? t(`preview.diag.${code}`) : t("preview.diag.fallback", {code});
  // Only authoritative UTC instants are formatted, in the operator's locale.
  const formatInstant = (value: string | null): string => {
    if (!value) return t("common.none");
    const epoch = epochOf(value);
    if (epoch === null) return value;
    try {
      return new Intl.DateTimeFormat(locale.value, {dateStyle: "medium", timeStyle: "short"}).format(new Date(epoch));
    } catch (_) {
      return value;
    }
  };
  const formatUtc = (value: string): string => {
    const m = UTC_PARTS.exec(value);
    return m ? `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}` : value;
  };
  const formatDate = (value: string | null): string => {
    if (!value) return t("common.none");
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!m) return value;
    try {
      return new Intl.DateTimeFormat(locale.value, {dateStyle: "medium", timeZone: "UTC"}).format(new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])));
    } catch (_) {
      return value;
    }
  };
  const pick = (label: LabelSet): string =>
    nonBlank(label[locale.value]) || nonBlank(label.ro) || nonBlank(label.en) || nonBlank(label.ru);
  // Nullable platform `name`/`short` stay null in the DTO; only the UI falls back.
  const platformLabel = (platform: {id: number; name: string | null; short: string | null}): string =>
    nonBlank(platform.name) || nonBlank(platform.short) || t("catalog.platform", {id: platform.id});
  const segmentLabel = (segment: {from_stop_id: number; to_stop_id: number; from_label: LabelSet; to_label: LabelSet}): string =>
    t("catalog.segment", {
      from: pick(segment.from_label) || t("catalog.stop", {id: segment.from_stop_id}),
      to: pick(segment.to_label) || t("catalog.stop", {id: segment.to_stop_id}),
    });
  const categoryLabel = (category: {id: number; label: LabelSet}): string => pick(category.label) || `#${category.id}`;
  return {
    t, has, tError, tDiagnostic, formatInstant, formatDate, formatUtc, pick,
    platformLabel, segmentLabel, categoryLabel, locale, elementLocale,
  };
}
